/**
 * GlobalAISearch — TOP-BAR natural-language search across ALL tenant data.
 * Distinct from the existing AIPowerSearch (which is module-scoped).
 * Shortcut: Cmd/Ctrl + Shift + K.
 */
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, X } from "lucide-react";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";

export default function GlobalAISearch() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const run = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.post("/ai-search/query", { query, limit: 20 });
      setResults(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Search failed");
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const goToResult = (r) => {
    const typeRoutes = {
      ra_tasks: "/ra/tracker",
      process_bia: "/operational-bia",
      application_profiles: "/application-profiles",
      exercises: "/exercise-calendar",
      risk_assessments: "/risk-assessment",
    };
    navigate(typeRoutes[r.type] || "/dashboard");
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
        data-testid="global-ai-search-trigger"
      >
        <Sparkles className="w-4 h-4 text-sky-600" />
        <span>Ask AI...</span>
        <kbd className="hidden md:inline-block ml-2 px-1.5 py-0.5 text-[10px] font-semibold bg-white border border-slate-300 rounded">
          ⌘⇧K
        </kbd>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center pt-20"
      onClick={() => setOpen(false)} data-testid="global-ai-search-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[70vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <form onSubmit={run} className="p-4 border-b flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-sky-600 shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='e.g., "processes with RTO under 4h" or "overdue RA tasks"'
            className="flex-1 border-0 focus-visible:ring-0 text-base"
            data-testid="global-ai-search-input"
          />
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-sky-600" />
          ) : (
            <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600" data-testid="global-ai-search-close">
              <X className="w-5 h-5" />
            </button>
          )}
        </form>

        <div className="flex-1 overflow-y-auto">
          {!results && !error && !loading && (
            <div className="p-6 text-sm text-slate-500 space-y-2">
              <p className="font-semibold text-slate-700">Try asking:</p>
              {["applications with highest residual risk",
                "processes marked Tier 1",
                "exercises scheduled next 30 days",
                "overdue RA tasks"].map((q) => (
                <button key={q} onClick={() => setQuery(q)}
                  className="block w-full text-left hover:bg-slate-50 p-2 rounded">
                  → {q}
                </button>
              ))}
            </div>
          )}
          {error && <div className="p-6 text-red-600 text-sm" data-testid="global-ai-search-error">{error}</div>}
          {results && (
            <div>
              <div className="px-4 py-3 bg-slate-50 border-b" data-testid="global-ai-search-plan">
                <p className="text-xs text-slate-500">
                  <Sparkles className="w-3 h-3 inline mr-1 text-sky-600" />
                  {results.plan?.intent_summary || "—"}
                </p>
                <p className="text-[10px] text-slate-400 mt-1">
                  {results.count} result(s) &middot; source: {results.plan?._source}
                </p>
              </div>
              {results.results?.length === 0 ? (
                <div className="p-6 text-sm text-slate-500 text-center">No matches.</div>
              ) : (
                <div className="divide-y">
                  {results.results.map((r, i) => (
                    <button key={r.id || i} onClick={() => goToResult(r)}
                      className="w-full text-left p-3 hover:bg-slate-50 transition"
                      data-testid={`global-ai-search-result-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{r.label}</p>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {Object.entries(r)
                              .filter(([k]) => !["id","label","type"].includes(k))
                              .slice(0,3)
                              .map(([k,v]) => `${k}: ${v}`)
                              .join(" · ")}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-[10px] ml-2">{r.type}</Badge>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
